import { Directive, Input, TemplateRef, ViewContainerRef } from '@angular/core';
import { ROLES_KEYS, SupportingCompanyAccessLevel } from '@interfaces';
import { UserAuthFacade } from '@state/user-auth';
import { firstValueFrom } from 'rxjs';
import { filter, take } from 'rxjs/operators';

interface RequiredSupportingCompanyDirectiveOpts {
  companyId: string;
  requiredAccessLevel: SupportingCompanyAccessLevel;
}

@Directive({
  selector: '[appRequiredSupportingCompany]',
})
export class RequiredSupportingCompanyDirective {
  @Input() set appRequiredSupportingCompany(opts: RequiredSupportingCompanyDirectiveOpts | null | undefined) {
    if (!opts?.companyId || !opts?.requiredAccessLevel) {
      this.viewContainer.createEmbeddedView(this.templateRef);
      return;
    }

    this.renderContainerIfConditionsMet(opts);
  }

  constructor(
    private readonly templateRef: TemplateRef<any>,
    private readonly viewContainer: ViewContainerRef,
    private readonly userAuthFacade: UserAuthFacade
  ) {}

  private async renderContainerIfConditionsMet({
    requiredAccessLevel,
    companyId,
  }: RequiredSupportingCompanyDirectiveOpts): Promise<boolean> {
    try {
      const myself = await firstValueFrom(
        this.userAuthFacade.myself$.pipe(
          filter(myself => !!myself?.id),
          take(1)
        )
      );

      if (myself?.role?.key === ROLES_KEYS.Admin) {
        this.viewContainer.createEmbeddedView(this.templateRef);
        return true;
      }

      if (!myself?.supportingCompanies?.length) {
        console.error(`Employee has no 'supportingCompanies' array populated`);
        this.viewContainer.clear();
        return false;
      }

      const hasRequiredAccess = myself.supportingCompanies.find(
        sc => sc[requiredAccessLevel] && sc.companyId === companyId
      );
      if (!hasRequiredAccess) {
        this.viewContainer.clear();
        return false;
      }

      this.viewContainer.createEmbeddedView(this.templateRef);
      return true;
    } catch (error) {
      console.error(`Error reading 'supportingCompanies' array from myself: `, error);
      this.viewContainer.clear();
      return false;
    }
  }
}
